import { isEmpty } from '@intelligence/v3/utils/common';
import { formatDateStr } from '@intelligence/v3/utils/date';
import { LANGUAGE_EN } from '@intelligence/_config/config';
import { buildDownloadDataNew } from '@intelligence/utils/commonUtils';
import { decimalFormat } from '@home/utils/common';
import { MINI_GAME_RANKING_KEY_LIST_DOUYIN } from '@intelligence/v3/pages/ReusePages/CasualMiniGameRankings/config';

type TExportLanguage = 'cn' | 'en';

const EXPORT_TEXT_MAP = {
  cn: {
    gameName: '游戏名称',
    gameDescription: '游戏描述',
    companyName: '公司名称',
    tags: '标签',
    date: '日期',
    primaryGenre: '主品类',
    subGenre: '子品类',
    topPopularRank: '人气榜',
    topPopularRank30DAvg: '人气榜30天平均排名',
    bestSellingRank: '畅销榜',
    bestSellingRank30DAvg: '畅销榜30天平均排名',
    mostPlayedRank: '畅玩榜',
    mostPlayedRank30DAvg: '畅玩榜30天平均排名',
    douyinTopPopularRank: '热门榜',
    douyinTopPopularRank30DAvg: '热门榜30天平均排名',
    newReleaseRank: '新游榜',
    newReleaseRank30DAvg: '新游榜30天平均排名',
    rankChange: '排名变化',
    topForDays: (dayNum: number) => `霸榜${dayNum}天`,
    newEntry: '新进榜',
  },
  en: {
    gameName: 'Game Name',
    gameDescription: 'Game Description',
    companyName: 'Company Name',
    tags: 'Tags',
    date: 'Date',
    primaryGenre: 'Primary Genre',
    subGenre: 'Sub Genre',
    topPopularRank: 'Top Popular Rank',
    topPopularRank30DAvg: 'Top Popular Rank 30D Avg',
    bestSellingRank: 'Best Selling Rank',
    bestSellingRank30DAvg: 'Best Selling Rank 30D Avg',
    mostPlayedRank: 'Most Played Rank',
    mostPlayedRank30DAvg: 'Most Played Rank 30D Avg',
    douyinTopPopularRank: 'Top Popular Rank',
    douyinTopPopularRank30DAvg: 'Top Popular Rank 30D Avg',
    newReleaseRank: 'New Release Rank',
    newReleaseRank30DAvg: 'New Release Rank 30D Avg',
    rankChange: 'Rank Change',
    topForDays: (dayNum: number) => `Top for ${dayNum} Days`,
    newEntry: 'New Entry',
  },
};

const SHEET_NAME_MAP: Record<TExportLanguage, Record<string, string>> = {
  cn: {
    wechat_popularity: '微信小游戏人气榜',
    wechat_bestseller: '微信小游戏畅销榜',
    wechat_most_played: '微信小游戏畅玩榜',
    douyin_popularity: '抖音小游戏热门榜',
    douyin_bestseller: '抖音小游戏畅销榜',
    douyin_fresh_game: '抖音小游戏新游榜',
  },
  en: {
    wechat_popularity: 'WeChat Top Popular',
    wechat_bestseller: 'WeChat Best Selling',
    wechat_most_played: 'WeChat Most Played',
    douyin_popularity: 'Douyin Top Popular',
    douyin_bestseller: 'Douyin Best Selling',
    douyin_fresh_game: 'Douyin New Release',
  },
};

const formatList = <T extends IGenreMarketProfile>(list?: T[] | null): T[] => {
  if (isEmpty(list)) {
    return [];
  }
  return list!;
};

const getExportLanguage = (downloadLanguage?: string): TExportLanguage => {
  return downloadLanguage === LANGUAGE_EN ? 'en' : 'cn';
};

const getSheetName = (topchartName: string, language: TExportLanguage, fallback?: string): string => {
  return SHEET_NAME_MAP[language]?.[topchartName] || fallback || 'Sheet1';
};

export const getWeixinStoreRankMoreConfig = (data: any, params: any, allParams: any) => {
  const granularity = params?.granularity || 'daily';
  const isTopPopular = params?.minigame_types?.includes('wechat_popularity');
  const isBestSelling = params?.minigame_types?.includes('wechat_bestseller');
  const isMostPlayed = params?.minigame_types?.includes('wechat_most_played');
  const isTopPopularDouyin = params?.minigame_types?.includes('douyin_popularity');
  const isBestSellingDouyin = params?.minigame_types?.includes('douyin_bestseller');
  const isNewReleaseDouyin = params?.minigame_types?.includes('douyin_fresh_game');
  const isCompanyShow = allParams?.company_show === 1;
  const isGenreShow = allParams?.genre_show === 1;
  const isDouYin = MINI_GAME_RANKING_KEY_LIST_DOUYIN.includes(allParams?.topchart_name);
  const language = getExportLanguage(allParams?.download_language);
  const exportTexts = EXPORT_TEXT_MAP[language];
  const commonSheetConfig = [
    {
      title: exportTexts.date,
      key: (item: any) => {
        const itemDate = item?.date || '-';
        return granularity === 'monthly' ? formatDateStr(itemDate, 'YYYY-MM') : itemDate;
      },
    },
    {
      title: exportTexts.gameName,
      key: (item: any) => {
        if (language === 'en') {
          return item?.app_name_en || item?.app_name || '-';
        }
        return item?.app_name || item?.app_name_en || '-';
      },
    },
    {
      title: exportTexts.companyName,
      key: (item: any) => {
        if (language === 'en') {
          return item?.publisher_en || item?.publisher || '-';
        }
        return item?.publisher || item?.publisher_en || '-';
      },
      when: () => isCompanyShow && !isDouYin,
    },
    {
      title: exportTexts.gameDescription,
      key: (item: any) => {
        if (language === 'en') {
          return item?.app_desc_en || item?.app_desc || '-';
        }
        return item?.app_desc || item?.app_desc_en || '-';
      },
      when: () => isDouYin,
    },
    {
      title: exportTexts.tags,
      key: (item: any) => {
        if ((item?.consecutive_days || 0) > 0) {
          return exportTexts.topForDays(item.consecutive_days);
        }
        if (item?.rank_change === '新进榜') {
          return exportTexts.newEntry;
        }
        return '';
      },
    },
    {
      title: exportTexts.topPopularRank,
      key: (item: any) => item?.game_rank || '-',
      when: () => isTopPopular,
    },
    {
      title: exportTexts.bestSellingRank,
      key: (item: any) => item?.game_rank || '-',
      when: () => isBestSelling,
    },
    {
      title: exportTexts.mostPlayedRank,
      key: (item: any) => item?.game_rank || '-',
      when: () => isMostPlayed,
    },
    {
      title: exportTexts.douyinTopPopularRank,
      key: (item: any) => item?.game_rank || '-',
      when: () => isTopPopularDouyin,
    },
    {
      title: exportTexts.bestSellingRank,
      key: (item: any) => item?.game_rank || '-',
      when: () => isBestSellingDouyin,
    },
    {
      title: exportTexts.newReleaseRank,
      key: (item: any) => item?.game_rank || '-',
      when: () => isNewReleaseDouyin,
    },
    {
      title: exportTexts.rankChange,
      key: (item: any) => {
        if (!item?.rank_change) {
          return '-';
        }
        if (item.rank_change === '新进榜') {
          return exportTexts.newEntry;
        }
        const num = Number(decimalFormat(item.rank_change, 1, false, ''));
        if (Number.isNaN(num)) {
          return item.rank_change;
        }
        if (num === 0) {
          return '=';
        }
        return num;
      },
      when: () => allParams?.ratio_show === 1,
    },
    {
      title: exportTexts.topPopularRank30DAvg,
      key: (item: any) => decimalFormat(item?.avg_rank_30d, 1, false, '-'),
      when: () => isTopPopular,
    },
    {
      title: exportTexts.bestSellingRank30DAvg,
      key: (item: any) => decimalFormat(item?.avg_rank_30d, 1, false, '-'),
      when: () => isBestSelling,
    },
    {
      title: exportTexts.mostPlayedRank30DAvg,
      key: (item: any) => decimalFormat(item?.avg_rank_30d, 1, false, '-'),
      when: () => isMostPlayed,
    },
    {
      title: exportTexts.douyinTopPopularRank30DAvg,
      key: (item: any) => decimalFormat(item?.avg_rank_30d, 1, false, '-'),
      when: () => isTopPopularDouyin,
    },
    {
      title: exportTexts.bestSellingRank30DAvg,
      key: (item: any) => decimalFormat(item?.avg_rank_30d, 1, false, '-'),
      when: () => isBestSellingDouyin,
    },
    {
      title: exportTexts.newReleaseRank30DAvg,
      key: (item: any) => decimalFormat(item?.avg_rank_30d, 1, false, '-'),
      when: () => isNewReleaseDouyin,
    },
    {
      title: exportTexts.primaryGenre,
      key: (item: any) => {
        if (language === 'en') {
          return item?.minigame_genre_en || item?.minigame_genre || '';
        }
        return item?.minigame_genre || item?.minigame_genre_en || '';
      },
      when: () => isGenreShow && !isDouYin,
    },
    {
      title: exportTexts.subGenre,
      key: (item: any) => {
        if (language === 'en') {
          return item?.minigame_sub_genre_en || item?.minigame_sub_genre || '';
        }
        return item?.minigame_sub_genre || item?.minigame_sub_genre_en || '';
      },
      when: () => isGenreShow && !isDouYin,
    },
  ];

  return {
    stat: {
      sheetName: getSheetName(allParams?.topchart_name, language, allParams?.dynamicText),
      style: 'normal',
      data: buildDownloadDataNew(commonSheetConfig, formatList(data?.list)),
    },
  };
};
